import { Link } from 'react-router-dom';

/**
 * Closing card shown in place of the composer when a conversation
 * has ended. The point is closure — not continuation. No new chat
 * input here.
 *
 * Tone shifts by endReason:
 *   - graduated      → warm gold; "thanks for showing up"
 *   - member_ended   → muted gold; same family but no celebration
 *   - abandoned      → wistful sage; "your match stepped away"
 *   - member_blocked → muted clay; quiet acknowledgement
 *   - user_banned    → quiet clay; safety-flavoured
 *   - csam_banned    → same as user_banned
 *   - founder_action → same as user_banned
 *
 * Always: a small italic ✦ fleuron, the peer's nickname, the date,
 * and a "what now" link back to Home so the user has a next step
 * rather than dead-ending.
 */
export function GoodbyeLetter({
  peerNickname,
  endReason,
  endedAt,
}: {
  peerNickname: string;
  endReason: string | null;
  endedAt: string | null;
}) {
  const tone = toneFor(endReason);
  const copy = copyFor(endReason, peerNickname);
  return (
    <aside className={`goodbye goodbye--${tone}`} role="note" aria-label="Conversation ended">
      <div className="goodbye-mark" aria-hidden="true">
        ✦
      </div>
      <p className="goodbye-eyebrow">{copy.eyebrow}</p>
      <h2 className="goodbye-headline">{copy.headline}</h2>
      <p className="goodbye-body">{copy.body}</p>
      {endedAt && (
        <p className="goodbye-meta muted">
          Ended{' '}
          {new Date(endedAt).toLocaleDateString(undefined, {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
          })}
        </p>
      )}
      {tone !== 'safety' && (
        <p className="goodbye-cta">
          <Link to="/">When you're ready, pair with someone new →</Link>
        </p>
      )}
    </aside>
  );
}

type Tone = 'graduation' | 'ending' | 'abandoned' | 'blocked' | 'safety';

function toneFor(reason: string | null): Tone {
  switch (reason) {
    case 'graduated':
      return 'graduation';
    case 'member_ended':
      return 'ending';
    case 'abandoned':
      return 'abandoned';
    case 'member_blocked':
      return 'blocked';
    case 'user_banned':
    case 'csam_banned':
    case 'founder_action':
      return 'safety';
    default:
      return 'ending';
  }
}

function copyFor(
  reason: string | null,
  peer: string,
): {
  eyebrow: string;
  headline: string;
  body: string;
} {
  switch (reason) {
    case 'graduated':
      return {
        eyebrow: 'A graceful ending',
        headline: `Thanks for showing up for ${peer}.`,
        body: 'You finished a conversation well — that itself is a small, meaningful thing. The messages here will stay; nothing is going to come along and disturb them.',
      };
    case 'member_ended':
      return {
        eyebrow: 'Conversation closed',
        headline: `You ended your conversation with ${peer}.`,
        body: "That's okay. Ending well is part of the practice. Their messages stay where they are, and they'll be matched with someone new the same way you will.",
      };
    case 'abandoned':
      return {
        eyebrow: 'Your match stepped away',
        headline: `${peer} didn't return.`,
        body: "Sometimes another person can't carry a conversation forward — work, school, hard weeks. It isn't a comment on you. You'll be paired with someone new on the next matching pass.",
      };
    case 'member_blocked':
      return {
        eyebrow: 'No longer paired',
        headline: 'This conversation has ended.',
        body: "You won't be matched with this person again. The messages here stay so you can read back through them if you want. Anything you share with your next match is fresh.",
      };
    case 'user_banned':
    case 'csam_banned':
    case 'founder_action':
      return {
        eyebrow: 'For your safety',
        headline: 'This conversation has been ended.',
        body: "A moderator stepped in. You don't owe this person any further engagement — your account is fine, and a fresh pairing will come on the next matching pass. If anything they said is sitting with you, please don't carry it alone: call or text 988 any time, day or night.",
      };
    default:
      return {
        eyebrow: 'Conversation closed',
        headline: 'This conversation has ended.',
        body: "Their messages stay where they are. You'll be matched with someone new on the next pass.",
      };
  }
}
